/**
 * Audit Logging Utility
 * Logs security-relevant events for incident tracking and compliance
 */

import { auth, db } from '../firebaseConfig';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

const AUDIT_COLLECTION = 'audit_logs';
const MAX_LOG_SIZE = 10000; // Max 10KB per log entry

/**
 * Log levels
 */
export const LOG_LEVELS = {
  INFO: 'info',
  WARNING: 'warning',
  ERROR: 'error',
  CRITICAL: 'critical',
};

/**
 * Event types
 */
export const EVENT_TYPES = {
  // Authentication
  LOGIN_SUCCESS: 'login_success',
  LOGIN_FAILURE: 'login_failure',
  LOGOUT: 'logout',
  REGISTER: 'register',
  PASSWORD_RESET: 'password_reset',
  EMAIL_VERIFICATION: 'email_verification',
  
  // Authorization
  UNAUTHORIZED_ACCESS: 'unauthorized_access',
  PERMISSION_DENIED: 'permission_denied',
  
  // Data Operations
  DATA_CREATE: 'data_create',
  DATA_UPDATE: 'data_update',
  DATA_DELETE: 'data_delete',
  DATA_READ: 'data_read_sensitive',
  
  // Admin Actions
  SUPERADMIN_ACTION: 'superadmin_action',
  CONFIG_CHANGE: 'config_change',
  USER_MANAGEMENT: 'user_management',
  TEAM_MANAGEMENT: 'team_management',
  
  // Security
  RATE_LIMIT_EXCEEDED: 'rate_limit_exceeded',
  SUSPICIOUS_ACTIVITY: 'suspicious_activity',
  FILE_UPLOAD: 'file_upload',
  FILE_DELETE: 'file_delete',
  
  // System
  SYSTEM_ERROR: 'system_error',
};

/**
 * Create audit log entry
 * @param {string} eventType - Type of event (from EVENT_TYPES)
 * @param {string} level - Log level (from LOG_LEVELS)
 * @param {string} message - Human-readable message
 * @param {object} metadata - Additional metadata (will be sanitized)
 * @param {string} userId - User ID (optional, will use current user if not provided)
 */
export const logAuditEvent = async (eventType, level, message, metadata = {}, userId = null) => {
  try {
    const user = auth?.currentUser;
    const userEmail = user?.email || 'unknown';
    const userUid = userId || user?.uid || 'unknown';
    
    // Sanitize metadata to prevent logging sensitive data
    const sanitizedMetadata = sanitizeMetadata(metadata);
    
    // Truncate message if too long
    const truncatedMessage = message.length > 500 ? message.substring(0, 500) + '...' : message;
    
    const logEntry = {
      eventType,
      level,
      message: truncatedMessage,
      metadata: sanitizedMetadata,
      userId: userUid,
      userEmail: userEmail,
      timestamp: serverTimestamp(),
      ipAddress: await getClientIP(), // Will be null if not available
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
    };
    
    // Check log size
    const logSize = JSON.stringify(logEntry).length;
    if (logSize > MAX_LOG_SIZE) {
      console.warn('Audit log entry too large, truncating metadata');
      logEntry.metadata = { truncated: true, originalSize: logSize };
    }
    
    // Write to Firestore
    await addDoc(collection(db, AUDIT_COLLECTION), logEntry);
    
    // Also log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`[AUDIT ${level.toUpperCase()}] ${eventType}: ${truncatedMessage}`, sanitizedMetadata);
    }
  } catch (error) {
    // Don't throw errors from audit logging - use console as fallback
    console.error('Failed to write audit log:', error);
    console.log(`[AUDIT FALLBACK ${level.toUpperCase()}] ${eventType}: ${message}`, metadata);
  }
};

/**
 * Sanitize metadata to prevent logging sensitive information
 * @param {object} metadata - Metadata to sanitize
 * @returns {object} Sanitized metadata
 */
const sanitizeMetadata = (metadata) => {
  if (!metadata || typeof metadata !== 'object') return {};
  
  const sensitiveKeys = [
    'password',
    'token',
    'secret',
    'key',
    'apiKey',
    'accessToken',
    'refreshToken',
    'creditCard',
    'ssn',
    'email', // Sometimes sensitive
  ];
  
  const sanitized = { ...metadata };
  
  Object.keys(sanitized).forEach((key) => {
    const lowerKey = key.toLowerCase();
    
    // Remove sensitive fields
    if (sensitiveKeys.some(sk => lowerKey.includes(sk))) {
      sanitized[key] = '[REDACTED]';
    }
    
    // Truncate long strings
    if (typeof sanitized[key] === 'string' && sanitized[key].length > 200) {
      sanitized[key] = sanitized[key].substring(0, 200) + '...';
    }
    
    // Remove nested objects that are too deep
    if (typeof sanitized[key] === 'object' && sanitized[key] !== null) {
      try {
        const stringified = JSON.stringify(sanitized[key]);
        if (stringified.length > 1000) {
          sanitized[key] = '[TRUNCATED]';
        }
      } catch (e) {
        sanitized[key] = '[INVALID]';
      }
    }
  });
  
  return sanitized;
};

/**
 * Get client IP address (if available)
 * @returns {Promise<string|null>} IP address or null
 */
const getClientIP = async () => {
  // In a real implementation, you might get this from a server endpoint
  // For now, return null as client-side IP detection is limited
  return null;
};

/**
 * Log superadmin action
 * @param {string} action - Action description
 * @param {object} metadata - Additional metadata
 */
export const logSuperadminAction = async (action, metadata = {}) => {
  await logAuditEvent(
    EVENT_TYPES.SUPERADMIN_ACTION,
    LOG_LEVELS.CRITICAL,
    `Superadmin action: ${action}`,
    metadata
  );
};

/**
 * Log authentication event
 * @param {string} eventType - Event type (LOGIN_SUCCESS, LOGIN_FAILURE, etc.)
 * @param {string} message - Event message
 * @param {object} metadata - Additional metadata
 */
export const logAuthEvent = async (eventType, message, metadata = {}) => {
  const level = eventType.includes('FAILURE') || eventType.includes('UNAUTHORIZED') 
    ? LOG_LEVELS.WARNING 
    : LOG_LEVELS.INFO;
  
  await logAuditEvent(eventType, level, message, metadata);
};

/**
 * Log data modification
 * @param {string} operation - Operation type (create, update, delete)
 * @param {string} collection - Collection name
 * @param {string} documentId - Document ID
 * @param {object} metadata - Additional metadata
 */
export const logDataModification = async (operation, collection, documentId, metadata = {}) => {
  const eventType = operation === 'create' ? EVENT_TYPES.DATA_CREATE
    : operation === 'update' ? EVENT_TYPES.DATA_UPDATE
    : EVENT_TYPES.DATA_DELETE;
  
  await logAuditEvent(
    eventType,
    LOG_LEVELS.INFO,
    `Data ${operation}: ${collection}/${documentId}`,
    { collection, documentId, ...metadata }
  );
};

/**
 * Log security event
 * @param {string} eventType - Event type
 * @param {string} message - Event message
 * @param {object} metadata - Additional metadata
 */
export const logSecurityEvent = async (eventType, message, metadata = {}) => {
  await logAuditEvent(
    eventType,
    LOG_LEVELS.WARNING,
    message,
    metadata
  );
};

